import { useState } from 'react';
import { LogOut, AlertCircle } from 'lucide-react';
import { useAppendEvent } from '../../hooks/usePeople';
import { useIntentKey } from '../../hooks/useIntentKey';
import { Button } from '../ui/Button';
import { ApiError } from '../../lib/api';

/**
 * Modal for recording a LeftEarly event (CLAUDE.md §5).
 */
export function LeftEarlyModal({
  open,
  personId,
  personName,
  onClose,
  onRecorded,
}: {
  open: boolean;
  personId: string;
  personName: string;
  onClose: () => void;
  onRecorded?: () => void;
}) {
  const [reason, setReason] = useState('');
  const [returning, setReturning] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const append = useAppendEvent(personId);
  const intent = useIntentKey();

  if (!open) return null;

  async function submit() {
    if (!reason.trim()) {
      setError('Reason is required');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await append.mutateAsync({
        event_type: 'LeftEarly',
        payload: {
          reason: reason.trim(),
          returning,
        },
        client_event_id: intent.key,
      } as Parameters<typeof append.mutateAsync>[0]);
      intent.renew();
      onRecorded?.();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'failed to record');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="left-early-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-fg/40 p-4 backdrop-blur-sm aurora-fade-in"
    >
      <div className="w-full max-w-md overflow-hidden rounded-token-lg border border-line/10 bg-surface shadow-token-lg">
        <header className="flex items-center gap-3 border-b border-divider/10 bg-warning-soft/15 px-5 py-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-token-sm bg-warning/15 text-warning">
            <LogOut className="h-5 w-5" />
          </div>
          <div>
            <h2 id="left-early-title" className="text-base font-semibold tracking-tight text-fg">
              Mark left early
            </h2>
            <p className="text-xs text-fg-muted">
              for <strong>{personName}</strong>
            </p>
          </div>
        </header>

        <div className="space-y-4 p-5 sm:p-6">
          <label className="block text-sm">
            <span className="font-medium text-fg">Reason</span>
            <textarea
              value={reason}
              rows={3}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. returning Thursday for tools"
              className="mt-1.5 block w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm text-fg shadow-token-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-ring/30"
            />
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-token-sm border border-line/10 p-3 transition-colors hover:bg-surface-2">
            <input
              type="checkbox"
              checked={returning}
              onChange={(e) => setReturning(e.target.checked)}
              className="h-4 w-4 rounded border-line-strong/30 accent-accent"
            />
            <span className="text-sm font-medium text-fg">
              Expected back to finish
            </span>
          </label>

          {error ? (
            <div className="flex items-start gap-2 rounded-token-sm bg-danger-soft/15 px-3 py-2 text-sm text-danger-soft-fg">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-divider/10 bg-surface-2 px-5 py-3">
          <Button variant="secondary" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="warning"
            size="md"
            iconLeft={<LogOut className="h-4 w-4" />}
            loading={submitting}
            onClick={submit}
          >
            Record
          </Button>
        </footer>
      </div>
    </div>
  );
}
